import {
  PolicyItem,
  PolicyItemInTable,
  ServiceCollection,
  TargetCapacity,
  SalesforceTargets,
  PolicyHolderTypeEnum,
} from '../../utils/types';
import {
  getPolicyName,
  replaceProductionNameInPolicyEntity,
} from '../../utils/fns';
import {
  COLLABORATION_POLICY_TYPE_ID_NAME_MAP,
  MAIL_POLICY_TYPE_ID_NAME_MAP,
  PolicyTypeEnum,
} from '../../utils/constants';
import { t } from 'i18next';
import customConfig from 'src/config';

const CollectionAndServicesMap: { [key in ServiceCollection]: number[] } = {
  mail: customConfig.ui.policy.policyTemplateTypes.emailTypes || [
    1, 82, 21, 22,
  ],
  collaboration: customConfig.ui.policy.policyTemplateTypes
    .collaborationTypes || [2, 3, 4, 5, 6, 9, 10, 41, 42],
};

export const getInitPolicyList = (collectionType: ServiceCollection) => {
  let ret: { [key: string]: PolicyItemInTable[] } = {};
  if (collectionType === ServiceCollection.Collaboration) {
    ret = CollectionAndServicesMap['collaboration'].reduce(
      (acc: { [type: string]: PolicyItemInTable[] }, cur) => {
        acc[COLLABORATION_POLICY_TYPE_ID_NAME_MAP[cur]] = [];
        return acc;
      },
      {}
    );
  } else {
    ret = CollectionAndServicesMap['mail'].reduce(
      (acc: { [type: string]: PolicyItemInTable[] }, cur) => {
        if (
          [PolicyTypeEnum.GmailInline, PolicyTypeEnum.ExchangeInline].includes(
            cur
          )
        ) {
          const v = MAIL_POLICY_TYPE_ID_NAME_MAP[cur] as {
            0: string;
            1: string;
          };
          acc[v[0]] = [];
          acc[v[1]] = [];
        } else {
          acc[MAIL_POLICY_TYPE_ID_NAME_MAP[cur] as string] = [];
        }
        return acc as {};
      },
      {}
    );
  }
  return ret;
};

export const getPolicyTarget = (
  policy: PolicyItem
): {
  policyTargetCapacity: PolicyItemInTable['policyTargetCapacity'];
  policyTarget: string;
} => {
  const { policyTarget, policyType, policySubType } = policy;
  const policyTypeName = getPolicyName(policyType, policySubType);
  let policyTargetCapacity: PolicyItemInTable['policyTargetCapacity'] = '';
  let policyTargetString = '';

  try {
    if (
      [
        PolicyTypeEnum.SalesforceProduction,
        PolicyTypeEnum.SalesforceSandbox,
      ].includes(policyType)
    ) {
      const { policyTarget, holderType } = policy;
      const policyTargetSalesforce = policyTarget as any as SalesforceTargets;

      if (!policyTargetSalesforce || policyTargetSalesforce === null) {
        return {
          policyTargetCapacity: 'none',
          policyTarget: `policy.policyTarget.${policyTypeName}.none`,
        };
      }

      let stringApp;
      if (PolicyHolderTypeEnum.ATP === holderType) {
        if (
          policyTargetSalesforce.targetCommunities.capacity ===
            TargetCapacity.all &&
          policyTargetSalesforce.targetObjects.capacity ===
            TargetCapacity.all &&
          policyTargetSalesforce.targetProfiles.capacity === TargetCapacity.all
        ) {
          return {
            policyTargetCapacity,
            policyTarget: t(`policy.policyTarget.${policyTypeName}.all`),
          };
        }

        const stringAppList = [];
        if (
          policyTargetSalesforce.targetCommunities.capacity ===
            TargetCapacity.all &&
          policyTargetSalesforce.targetObjects.capacity === TargetCapacity.all
        ) {
          stringAppList.push(
            t(`policy.policyTarget.${policyTypeName}.allApps`)
          );
        } else if (
          policyTargetSalesforce.targetCommunities.capacity ===
          TargetCapacity.all
        ) {
          stringAppList.push(t('policy.policyTarget.label.allCommunities'));
        } else if (
          policyTargetSalesforce.targetObjects.capacity === TargetCapacity.all
        ) {
          stringAppList.push(t('policy.policyTarget.label.allObjects'));
        } else {
          if (
            policyTargetSalesforce.targetCommunities.targetGroups.length > 0
          ) {
            stringAppList.push(
              policyTargetSalesforce.targetCommunities.targetGroups
                .map((target) => target.title)
                .join(',')
            );
          }
          if (policyTargetSalesforce.targetCommunities.targets.length > 0) {
            stringAppList.push(
              policyTargetSalesforce.targetCommunities.targets
                .map((target) => target.title)
                .join(',')
            );
          }
          if (policyTargetSalesforce.targetObjects.targetGroups.length > 0) {
            stringAppList.push(
              policyTargetSalesforce.targetObjects.targetGroups
                .map((target) => target.title)
                .join(',')
            );
          }
          if (policyTargetSalesforce.targetObjects.targets.length > 0) {
            stringAppList.push(
              policyTargetSalesforce.targetObjects.targets
                .map((target) => target.title)
                .join(',')
            );
          }
        }
        stringApp =
          t('policy.policyTarget.label.app') + ': ' + stringAppList.join(',');
      } else {
        const stringObjectList = [];
        if (
          policyTargetSalesforce.targetObjects.capacity === TargetCapacity.all
        ) {
          stringObjectList.push(
            t(`policy.policyTarget.${policyTypeName}.allObjects`)
          );
        } else {
          stringObjectList.push(
            policyTargetSalesforce.targetObjects.targets
              .map((target) => target.title)
              .join(',')
          );
        }
        stringApp =
          t('policy.policyTarget.label.objects') +
          ': ' +
          stringObjectList.join(',');
      }

      const stringProfileList = [];
      if (
        policyTargetSalesforce.targetProfiles.capacity === TargetCapacity.all
      ) {
        stringProfileList.push(
          t(`policy.policyTarget.${policyTypeName}.allProfile`)
        );
      } else {
        stringProfileList.push(
          policyTargetSalesforce.targetProfiles.targets
            .map((target) => target.title)
            .join(',')
        );
      }
      const stringProfile =
        t('policy.policyTarget.label.profiles') +
        ': ' +
        stringProfileList.join(',');
      return {
        policyTargetCapacity,
        policyTarget: stringApp + ',' + stringProfile,
      };
    }
  } catch (error) {
    console.error(error);
  }

  try {
    if (policyTarget.capacity === TargetCapacity.empty) {
      policyTargetString = t(`policy.policyTarget.${policyTypeName}.none`);
      policyTargetCapacity = 'none';
    } else if (
      policyTarget.targetGroups.length === 0 &&
      policyTarget.targets.length === 0
    ) {
      if (
        [PolicyTypeEnum.ExchangeInline, PolicyTypeEnum.GmailInline].includes(
          policyType
        )
      ) {
        if (policyTarget.targetType === 'user') {
          policyTargetString = t(`policy.policyTarget.${policyTypeName}.all`);
        } else {
          policyTargetString = t('policy.policyTarget.allDomains');
        }
      } else {
        policyTargetString = t(`policy.policyTarget.${policyTypeName}.all`);
      }
    } else {
      policyTargetString += [
        ...policyTarget.targetGroups,
        ...policyTarget.targets,
      ]
        .map((target) => target.title)
        .join(',');
    }
  } catch (error) {
    console.error(error);
  }

  return {
    policyTargetCapacity,
    policyTarget: policyTargetString,
  };
};

export const transformRawPolicyData = (
  rawData: any[],
  collectionType: ServiceCollection
): { [key: string]: PolicyItemInTable[] } => {
  const data = replaceProductionNameInPolicyEntity(rawData);
  const initPolicyList = getInitPolicyList(collectionType);

  data.forEach((item: any) => {
    const name = getPolicyName(item.policyType, item.policySubType);
    if (name && initPolicyList[name]) {
      initPolicyList[name].push({
        ...item,
        ...getPolicyTarget(item),
      });
    }
  });

  return initPolicyList;
};
